/**
 * Reconciliation View — FIE Screen 4
 * Now uses real job-derived data with completeness indicators.
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertTriangle, Info } from "lucide-react";
import { useFIE } from "@/data/fie/context";
import { Currency, VarianceCell } from "./SharedComponents";

const COMPLETENESS_LABELS: Record<string, { label: string; className: string }> = {
  complete: { label: "Complete", className: "bg-success/10 text-success border-success/20" },
  partial: { label: "Partial", className: "bg-warning/10 text-warning border-warning/20" },
  incomplete: { label: "Incomplete", className: "bg-destructive/10 text-destructive border-destructive/20" },
};

export function ReconciliationView() {
  const { visibleReconciliation: reconciliation } = useFIE();

  if (reconciliation.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Reconciliation</h1>
          <p className="text-sm text-muted-foreground">Compare projected vs actual outcomes for closed periods</p>
        </div>
        <Card>
          <CardContent className="p-12 text-center">
            <AlertTriangle className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No reconciled periods available yet.</p>
            <p className="text-xs text-muted-foreground mt-1">Reconciliation data will appear after a period is closed and reviewed.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Reconciliation</h1>
        <p className="text-sm text-muted-foreground">
          Projected vs actual comparison — derived from real job data
        </p>
      </div>

      {/* Data integrity notice */}
      <div className="flex items-start gap-2 p-3 rounded-lg border bg-info/5 border-info/20">
        <Info className="h-4 w-4 text-info mt-0.5 shrink-0" />
        <p className="text-sm text-info">
          Actuals are derived from completed jobs in each period. Categories marked "Partial" or "Incomplete" indicate missing cost entries — these are shown transparently, not fabricated.
        </p>
      </div>

      {reconciliation.map(r => {
        const completeness = r.dataCompleteness;
        return (
          <Card key={r.periodId} className="border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  {r.periodLabel}
                </CardTitle>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>Reconciled by {r.reconciledBy}</span>
                  <span>•</span>
                  <span>{new Date(r.reconciledAt).toLocaleDateString()}</span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Metric</TableHead>
                    <TableHead className="text-right">Projected</TableHead>
                    <TableHead className="text-right">Actual</TableHead>
                    <TableHead className="text-right">Variance ($)</TableHead>
                    <TableHead className="text-right">Variance (%)</TableHead>
                    <TableHead>Data Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(["revenue", "costs", "netProfit", "distributions"] as const).map(key => {
                    const labels: Record<string, string> = { revenue: "Revenue", costs: "Total Costs", netProfit: "Net Profit", distributions: "Distributions" };
                    const status = completeness?.[key] || "incomplete";
                    const cfg = COMPLETENESS_LABELS[status];
                    return (
                      <TableRow key={key}>
                        <TableCell className="font-medium">{labels[key]}</TableCell>
                        <TableCell className="text-right"><Currency value={r.projected[key]} /></TableCell>
                        <TableCell className="text-right font-medium"><Currency value={r.actual[key]} /></TableCell>
                        <TableCell className="text-right"><VarianceCell value={r.varianceDollar[key]} /></TableCell>
                        <TableCell className="text-right"><VarianceCell value={r.variancePercent[key]} isPercent /></TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[10px] ${cfg.className}`}>{cfg.label}</Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {r.drivers.length > 0 && (
                <div className="p-3 rounded-lg border bg-muted/30">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Variance Drivers</p>
                  <ul className="space-y-1">
                    {r.drivers.map((d, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className="text-muted-foreground/50 mt-0.5">•</span>
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
